import React, { useState, useEffect } from 'react';
import { Form, Input, Upload } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUserDetails, updateProfile, updateProfilePic } from '../../store/slices/userSlice';
import CustomModal from '../../components/common/CustomModal';
import { PROFILE_IMG_PATH } from '../../actions/utilAction';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';

// TypeScript interfaces
interface RouteParams {
  userId: string;
}

interface UserDetails {
  _id: string;
  name: string;
  email: string;
  profilePhoto?: string;
  joinedDate?: string;
  [key: string]: any;
}

interface ProfileFormValues {
  name: string;
}

// Helper function to convert file to base64
const getBase64 = (img: RcFile, callback: (url: string) => void): void => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const UserProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { userId } = useParams<RouteParams>();
  const userDetailsFromStore = useAppSelector((state) => state.user.userDetails);

  const [form] = Form.useForm<ProfileFormValues>();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loading, setLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [changeImage, setChangeImage] = useState<boolean>(false);

  useEffect(() => {
    if (userId) {
      dispatch(getUserDetails({ userId }));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userDetailsFromStore) {
      setUserDetails(userDetailsFromStore as UserDetails);
      setLoading(false);
      setChangeImage(false);
      setImageUrl(userDetailsFromStore.profilePhoto || '');
    }
  }, [userDetailsFromStore]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _showModal = (): void => {
    setVisible(true);
    const user = userDetails || userDetailsFromStore;
    if (user) {
      form.setFieldsValue({
        name: user.name,
      });
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile>): void => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done' && info.file.originFileObj && userDetails) {
      getBase64(info.file.originFileObj, (url: string) => {
        setImageUrl(url);
        setChangeImage(true);
      });
      const formData = new FormData();
      formData.append('file', info.file.originFileObj);
      formData.append('email', userDetails.email);
      dispatch(updateProfilePic({ formData, userId: userDetails._id }));
    }
  };

  const handleOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      if (userDetails) {
        const obj = {
          name: values.name,
          userId: userDetails._id,
        };
        setUserDetails({ ...userDetails, name: values.name });
        setVisible(false);
        dispatch(updateProfile(obj));
      }
    } catch {
      // Validation failed - form will show errors
    }
  };

  const handleCancel = (): void => {
    setVisible(false);
  };

  const user = userDetails || { name: '', email: '', joinedDate: '' };
  const profilePic = imageUrl || (userDetailsFromStore as UserDetails)?.profilePhoto || '';

  return (
    <>
      <div className="overflowBox">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="PROFILE"
          subTitle=""
        />
        <div className="box box-default mb-4">
          <div className="box-body">
            <div className="row profileDetails">
              <div className="col-sm-6 col-md-6">
                <div className="row">
                  <div className="col-sm-6 col-md-4">
                    <Upload
                      disabled={true}
                      name="avatar"
                      listType="picture-card"
                      className="avatar-uploader"
                      showUploadList={false}
                      action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                      onChange={handleChange}
                    >
                      {changeImage ? (
                        <img
                          src={profilePic || 'http://placehold.it/380x500'}
                          alt="profile"
                          className="img-rounded img-responsive img-104"
                        />
                      ) : (
                        <img
                          src={profilePic ? PROFILE_IMG_PATH + profilePic : 'http://placehold.it/380x500'}
                          alt="profile"
                          className="img-rounded img-responsive img-104"
                        />
                      )}
                    </Upload>
                  </div>
                  <div className="col-sm-6 col-md-6">
                    <h4>{user.name} &nbsp;</h4>
                    <p>
                      <i className="fa fa-envelope"></i> {user.email}
                      <br />
                      <i className="fa fa-gift"></i> Joined: {moment(user.joinedDate).format('LL')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-md-6" style={{ textAlign: 'right' }}>
                {/* Edit icon - currently disabled */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        handleCancel={handleCancel}
        handleOk={handleOk}
        visible={visible}
        title="Profile Details"
        backBtnText="Back"
        submitBtnText="Submit"
      >
        <Form form={form} className="profile-form l_hgt0" layout="vertical">
          <Form.Item
            label="User Name"
            name="name"
            rules={[
              { transform: (value: string) => value?.trim() },
              { required: true, message: 'Please enter your name' },
            ]}
          >
            <Input placeholder="Enter Name" />
          </Form.Item>
        </Form>
      </CustomModal>
    </>
  );
};

export default UserProfile;
